import type { ShortVideoProperty } from '~/core/entities/property';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { mapPropertyToShortVideo } from '~/composables/shorts/mappers';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import {
	hasRecommendationReference,
	withDisplayInHomeShortsFallback
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

export interface ShortVideo {
	property: ShortVideoProperty;
	videoUrl: string | null;
	thumbnailUrl: string | null;
	images?: string[];
	videoWidth?: number;
	videoHeight?: number;
}

export interface FetchShortsResult {
	items: ShortVideo[];
	hasMore: boolean;
	allWatched?: boolean;
	totalAvailable?: number;
	appliedFilters: ShortsFilters;
}

const PER_PAGE = 15;

export interface FetchShortsOptions {
	enableRecommendationFallback?: boolean;
}

interface InternalFetchShortsResult extends FetchShortsResult {
	hasRequestError: boolean;
}

export function useFetchShorts() {
	const logger = useLogger('useFetchShorts');
	const { user } = useUserSession();
	const getUserId = () => {
		if (!import.meta.client) return undefined;
		return user.value?.id;
	};

	const getCookieData = useCookieData();

	async function requestVideos(
		filters: ShortsFilters,
		page: number,
		perPage: number
	): Promise<InternalFetchShortsResult> {
		const [result, error] = await nuxtPropertiesRepository.getShorts(
			{ ...filters, markUserWishlistsId: getUserId(), page, perPage },
			getCookieData()
		);

		if (error || !result) {
			logger.error('Failed to fetch shorts', {
				...(error
					? extractErrorLogContext(error)
					: { error_message: 'No shorts result returned from repository' }),
				filters
			});
			return {
				items: [],
				hasMore: false,
				appliedFilters: filters,
				hasRequestError: true
			};
		}

		const items = (result.properties ?? [])
			.map(mapPropertyToShortVideo)
			.filter((item): item is ShortVideo => item !== null);

		const pagination = result.pagination;
		const hasMorePages =
			(pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

		return {
			items,
			hasMore: hasMorePages,
			allWatched: result.allWatched,
			totalAvailable:
				result.totalAvailable ?? pagination?.filteredCount ?? items.length,
			appliedFilters: filters,
			hasRequestError: false
		};
	}

	async function fetchVideos(
		filters: ShortsFilters = {},
		page: number = 1,
		perPage?: number,
		options: FetchShortsOptions = {}
	): Promise<FetchShortsResult> {
		const effectivePerPage = perPage ?? PER_PAGE;
		const primaryResult = await requestVideos(filters, page, effectivePerPage);

		if (
			!primaryResult.hasRequestError &&
			!primaryResult.items.length &&
			options.enableRecommendationFallback &&
			hasRecommendationReference(filters)
		) {
			return requestVideos(
				withDisplayInHomeShortsFallback(filters),
				page,
				effectivePerPage
			);
		}

		return primaryResult;
	}

	return { fetchVideos };
}
